import React, { useEffect, useMemo, useState } from "react";
import "./DashboardContent.css";
import { apiUrl } from "./LoginSignup";
import {
  Box,
  IconButton,
  Switch,
  createTheme,
  ThemeProvider,
} from "@mui/material";
import { Brightness4, Brightness7 } from "@mui/icons-material";
import LineChart from "./LineChart";
import ProgressChart from "./ProgressChart";
import Loader from "./Loader";

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const DashboardContent = () => {
  const [userSession, setUserSession] = useState(null);
  const [totalBookings, setTotalBookings] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [monthlyRevenue, setMonthlyRevenue] = useState(Array(12).fill(0));
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [totalUsers, setTotalUsers] = useState(0);
  const [recentBookings, setRecentBookings] = useState([]);
  const [workflowSummary, setWorkflowSummary] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const session = JSON.parse(localStorage.getItem("userSession"));
    if (session) {
      setUserSession(session);
    } else {
      setLoading(false);
      console.error("User session not found");
    }
  }, []);

  useEffect(() => {
    if (userSession?.user_id) {
      fetchDashboardData(userSession);
    }
  }, [userSession]);

  const appTheme = createTheme({
    palette: {
      mode: darkMode ? "dark" : "light",
      primary: {
        main: darkMode ? "#90caf9" : "#1D4ED8",
      },
      secondary: {
        main: darkMode ? "#f48fb1" : "#1F2937",
      },
    },
  });

  const fetchDashboardData = async (session) => {
    setLoading(true);

    try {
      const response = await fetch(`${apiUrl}/booking/dashboard/stats`, {
        headers: {
          "Content-Type": "application/json",
          authorization: session.token,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch dashboard stats");
      }

      const data = await response.json();

      setTotalBookings(data.totalBookings || 0);
      setTotalRevenue(data.totalRevenue || 0);
      setMonthlyRevenue(data.monthlyRevenue || Array(12).fill(0));
      setRecentBookings(data.recentBookings || []);
      setTotalUsers(data.totalUsers || 0);
      setWorkflowSummary(data.workflowSummary || null);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const progressChartData = useMemo(() => {
    const buckets = workflowSummary?.buckets || {
      notStarted: 0,
      inProgress: 0,
      completed: 0,
    };

    return {
      labels: ["Not Started", "In Progress", "Completed"],
      data: [buckets.notStarted, buckets.inProgress, buckets.completed],
    };
  }, [workflowSummary]);

  const stageStatusChartData = useMemo(() => {
    const counts = workflowSummary?.stageStatusCounts || {
      Pending: 0,
      "In Progress": 0,
      Completed: 0,
    };

    return {
      labels: ["Pending", "In Progress", "Completed"],
      data: [counts.Pending, counts["In Progress"], counts.Completed],
    };
  }, [workflowSummary]);

  const averageProgress = workflowSummary?.averageProgress ?? 0;
  const currentMonthName = new Date().toLocaleString("default", {
    month: "long",
  });

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Loader />
      </div>
    );
  }

  return (
    <ThemeProvider theme={appTheme}>
      <Box className="dashboard-page">
        <div className="dashboard-header">
          <div className="dashboard-title-block">
            <h1>Dashboard</h1>
            <p>
              Overview of bookings, revenue, and workflow progress for{" "}
              {new Date().getFullYear()}.
            </p>
          </div>

          <div className="dashboard-theme-toggle">
            <span>{darkMode ? "Dark" : "Light"}</span>
            <Switch checked={darkMode} onChange={() => setDarkMode((v) => !v)} />
            <IconButton onClick={() => setDarkMode((v) => !v)} color="inherit" size="small">
              {darkMode ? <Brightness7 /> : <Brightness4 />}
            </IconButton>
          </div>
        </div>

        <div className="dashboard-stats-grid">
          <div className="dashboard-stat-card">
            <div className="dashboard-stat-label">Total Bookings</div>
            <div className="dashboard-stat-value">
              {Number(totalBookings || 0).toLocaleString()}
            </div>
            <div className="dashboard-stat-caption">Active bookings in scope</div>
          </div>

          <div className="dashboard-stat-card">
            <div className="dashboard-stat-label">Avg. Progress</div>
            <div className="dashboard-stat-value">{averageProgress}%</div>
            <div className="dashboard-stat-caption">Across all workflow stages</div>
          </div>

          <div className="dashboard-stat-card">
            <div className="dashboard-stat-label">Revenue ({currentMonthName})</div>
            <div className="dashboard-stat-value">
              {Number(monthlyRevenue[new Date().getMonth()] || 0).toLocaleString()}{" "}
              <span style={{ fontSize: "0.95rem", fontWeight: 600 }}>INR</span>
            </div>
            <div className="dashboard-stat-caption">Current month collections</div>
          </div>

          <div className="dashboard-stat-card">
            <div className="dashboard-stat-label">Total Users</div>
            <div className="dashboard-stat-value">
              {Number(totalUsers || 0).toLocaleString()}
            </div>
            <div className="dashboard-stat-caption">CRM users</div>
          </div>
        </div>

        <div className="dashboard-charts-grid">
          <div className="dashboard-chart-card">
            <h3>Booking Progress</h3>
            <p>Distribution by overall completion</p>
            <ProgressChart
              type="doughnut"
              labels={progressChartData.labels}
              data={progressChartData.data}
              colors={["#CBD5E1", "#1D4ED8", "#059669"]}
            />
          </div>

          <div className="dashboard-chart-card">
            <h3>Stage Status</h3>
            <p>All stages across active bookings</p>
            <ProgressChart
              type="bar"
              labels={stageStatusChartData.labels}
              data={stageStatusChartData.data}
              colors={["#F59E0B", "#1D4ED8", "#059669"]}
            />
          </div>

          <div className="dashboard-chart-card">
            <h3>Monthly Revenue</h3>
            <p>Year-to-date revenue trend</p>
            <LineChart
              labels={MONTH_LABELS}
              data={monthlyRevenue}
              label="Revenue"
              color="#1D4ED8"
            />
          </div>
        </div>

        <div className="dashboard-revenue-row">
          <div className="dashboard-revenue-card">
            <h3>Revenue Overview</h3>
            <p>Monthly and annual revenue snapshot</p>
            <LineChart
              labels={MONTH_LABELS}
              data={monthlyRevenue}
              label="Revenue"
              color="#2563EB"
            />
          </div>

          <div className="dashboard-revenue-card">
            <div className="dashboard-month-select">
              <select
                value={selectedMonth}
                onChange={(event) => setSelectedMonth(Number(event.target.value))}
              >
                {MONTH_LABELS.map((label, index) => (
                  <option value={index} key={label}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <h3>{MONTH_LABELS[selectedMonth]} Revenue</h3>
            <p>Selected month performance</p>
            <div className="dashboard-stat-value" style={{ marginTop: 12 }}>
              {Number(monthlyRevenue[selectedMonth] || 0).toLocaleString()} INR
            </div>

            <div
              className="dashboard-stat-caption"
              style={{ marginTop: 18, marginBottom: 8 }}
            >
              Total {new Date().getFullYear()} revenue
            </div>
            <div className="dashboard-stat-value" style={{ fontSize: "1.35rem" }}>
              {Number(totalRevenue || 0).toLocaleString()} INR
            </div>
          </div>
        </div>

        <div className="dashboard-table-card">
          <h3>Recent Bookings</h3>
          <p>Latest bookings with live workflow progress</p>

          <div className="dashboard-table-wrap">
            <table className="dashboard-table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>BDM</th>
                  <th>Booking Date</th>
                  <th>Progress</th>
                </tr>
              </thead>
              <tbody>
                {recentBookings.length === 0 ? (
                  <tr>
                    <td colSpan={4}>No recent bookings found.</td>
                  </tr>
                ) : (
                  recentBookings.map((booking) => {
                    const percent = booking.workflow_progress?.percent ?? 0;

                    return (
                      <tr key={booking._id}>
                        <td>{booking.company_name || "N/A"}</td>
                        <td>{booking.bdm || "N/A"}</td>
                        <td>
                          {booking.createdAt
                            ? new Date(booking.createdAt).toLocaleDateString()
                            : "N/A"}
                        </td>
                        <td>
                          <div className="dashboard-progress-pill">
                            <div className="dashboard-progress-bar">
                              <div
                                className="dashboard-progress-fill"
                                style={{ width: `${percent}%` }}
                              />
                            </div>
                            <span className="dashboard-progress-text">{percent}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Box>
    </ThemeProvider>
  );
};

export default DashboardContent;
